import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AutoComplete } from 'primeng/autocomplete';
import { Subject, of, timer } from 'rxjs';
import { catchError, debounce, switchMap, takeUntil, tap } from 'rxjs/operators';

import { ItemsService } from '../../../../../../services/financeiro/item.service';
import { MOEDA_CONFIG, Moeda, MoedaConfig } from '../../../../../../enums/moeda.enum';

/**
 * Busca inline (type-ahead) de item da aba "Nova venda" do Operar Cliente.
 *
 * Widget de busca e só isso: não conhece carrinho nem regra de negócio — emite
 * o item escolhido para o pai (OperarVendaComponent), que decide o destino.
 *
 * Substitui o antigo botão "+ Adicionar item"; o modal completo continua
 * acessível pela lupa do campo e pelo rodapé "Ver lista completa".
 */
@Component({
  selector: 'app-operar-venda-item-busca',
  templateUrl: './operar-venda-item-busca.component.html',
  styleUrls: ['./operar-venda-item-busca.component.scss'],
})
export class OperarVendaItemBuscaComponent implements OnInit, OnDestroy {
  /**
   * Moeda do CLIENTE, para formatar o preço na sugestão.
   *
   * Vem do pai, NUNCA de `GET /configuracaoEmpresa/sessao`: a sessão responde
   * pela empresa do CONTADOR e derivar dela já causou preço 100x maior em USD
   * (f106908d).
   */
  @Input() cfgMoeda: MoedaConfig = MOEDA_CONFIG[Moeda.GUARANI];

  /** Bloqueia o campo enquanto a venda está sendo gravada (`salvando` no pai). */
  @Input() disabled = false;

  /**
   * Id do <input> interno, para o pai apontar um `<label for>` real — o id
   * gerado pelo PrimeNG não é previsível. O rótulo mora no pai para reusar o
   * `.form-item > label` dos demais campos da aba.
   */
  @Input() inputId = 'venda-item-busca';

  /**
   * Mostra o atalho "Criar novo item" no rodapé do dropdown.
   *
   * Default `false`: telas que não devem oferecer cadastro montam o componente
   * sem ligar nada. Hoje só o `operar-venda` liga.
   */
  @Input() permitirCriarItem = false;

  /**
   * Exibe o contador "Mostrando X de Y" no hint. Ele persiste com o painel
   * fechado (as sugestoes ficam em memoria) e, conforme o layout, le-se como
   * contagem de OUTRA tabela — dai poder desliga-lo. Erro e "buscando..."
   * continuam sempre visiveis.
   */
  @Input() exibirContadorDeResultados = true;

  /**
   * Mostra o codigo interno (#123) na linha da sugestao.
   *
   * Default `true`: as telas que nao tem seletor de colunas continuam como
   * estavam. O pedido passa a visibilidade da coluna `Cod.`, pelo mesmo motivo
   * do input abaixo — sugestao e tabela mostram os mesmos dados.
   */
  @Input() exibirCodigoDoItem = true;

  /**
   * Mostra o codigo de barras na linha da sugestao.
   *
   * Quem decide e o PAI, para a sugestao acompanhar a tabela: no pedido o campo
   * so exibe o codigo de barras quando a coluna correspondente esta ligada. Em
   * telas sem essa coluna o dado seria ruido na linha.
   */
  @Input() exibirCodigoDeBarras = false;

  /**
   * Quando `false`, o foco após escolher um item passa a ser do pai.
   *
   * A linha de lançamento precisa mandar o foco ao campo Qtd., e o
   * `focarInput()` daqui roda DENTRO do `emit()` — depois do handler do pai,
   * vencendo-o. Default `true` preserva quem não usa a linha.
   */
  @Input() devolverFocoAposEscolher = true;

  /**
   * Item escolhido no dropdown.
   *
   * ⚠️ Emite o objeto CRU da API, sem `map`/spread/pick: o pai lê `custo`,
   * `tipoDeImposto`, `tipoItem` e `saldo` para montar a linha, e qualquer
   * projeção aqui gravaria a nota com custo zerado e imposto nulo.
   *
   * `any` e não `ItemDTO` porque o model não descreve o payload real
   * (`tipoItem`/`tipoDeImposto` chegam como `{ enum }`; `grupo`/`subGrupo` nem
   * estão declarados) — mesma escolha do modal.
   */
  @Output() itemEscolhido = new EventEmitter<any>();

  /**
   * Item escolhido por LEITURA DE CÓDIGO DE BARRAS, não por clique no dropdown.
   *
   * Separado porque o bipe vai DIRETO ao carrinho: o leitor emite Enter sozinho
   * e o operador não teria como dar o segundo Enter que a linha de lançamento
   * exige.
   *
   * A classificação da origem é feita aqui (só este componente tem o termo
   * digitado e o total de resultados); a decisão é do pai. Ver
   * `ehLeituraDeCodigoDeBarras()`.
   */
  @Output() itemEscolhidoViaCodigo = new EventEmitter<any>();

  /**
   * Item escolhido com MULTIPLICADOR: o operador digitou "3*401" (ou o codigo de
   * barras) e quer 3 unidades de uma vez.
   *
   * Output proprio porque a quantidade nao cabe nos outros dois, que emitem o
   * produto cru. So e usado quando o pai o escuta — quem nao escuta continua
   * recebendo pelos Outputs de sempre, uma unidade por escolha.
   */
  @Output() itemEscolhidoComQuantidade = new EventEmitter<{ produto: any; quantidade: number }>();

  /**
   * O usuário desfez a seleção — clicou no `×` ou apagou o texto.
   *
   * O nome do item fica no input até ser lançado, então esvaziar o campo
   * significa "não há mais item". Sem este aviso a linha continuaria exibindo o
   * preço de um item que o campo já não mostra.
   */
  @Output() selecaoLimpa = new EventEmitter<void>();

  /** Pedido de abrir o modal completo (lupa ou rodapé do dropdown). */
  @Output() abrirLista = new EventEmitter<void>();

  /**
   * Pedido de abrir o diálogo "Criar novo Item" (rodapé do dropdown). Só sai
   * com `permitirCriarItem` ligado; quem cria é o pai.
   */
  @Output() criarItem = new EventEmitter<void>();

  @ViewChild(AutoComplete) private ac?: AutoComplete;

  /** Model do campo: string enquanto digita, objeto após selecionar, null ao limpar. */
  termo: any = null;

  /** Sugestões exibidas no dropdown (itens CRUS da API). */
  sugestoes: any[] = [];

  /** Total de itens que casam com o termo (a página traz só os 8 primeiros). */
  total = 0;

  /**
   * Último termo enviado à busca. O `ngModel` vira o OBJETO escolhido no
   * instante da seleção, então o texto digitado se perderia justo quando é
   * preciso compará-lo com o código de barras do item.
   */
  private ultimoTermo = '';

  carregando = false;
  erroBusca = false;

  /** Guarda de reentrância do `limparSelecao()` — ver o comentário lá. */
  private limpandoProgramaticamente = false;

  private termo$ = new Subject<string>();
  private destruido$ = new Subject<void>();

  constructor(private itemService: ItemsService) {}

  ngOnInit(): void {
    this.termo$
      .pipe(
        // Digitação espera 300ms; a LISTA PRÉVIA (termo vazio, disparada pelo
        // foco) sai na hora. Debounce no caminho da prévia daria a impressão de
        // campo travado — o operador clica e não acontece nada por 0,3s.
        debounce((termo) => timer(termo.trim() ? 300 : 0)),
        // ⛔ NÃO acrescentar `distinctUntilChanged()`, `filter`, `throttle` nem
        // `take`. O p-autoComplete liga `loading` em `search()` e só desliga em
        // `handleSuggestionsChange()`, disparado exclusivamente pela atribuição
        // de `[suggestions]` — que também é o único ponto que chama `show()`.
        // Emissão suprimida = spinner preso e painel que nunca abre, sem erro no
        // console. Caso real: buscar "coca", dar Enter e digitar "coca" de novo.
        // INVARIANTE: toda emissão deste pipe termina numa atribuição de
        // `this.sugestoes` — no sucesso, no erro e no vazio.
        tap(() => {
          this.carregando = true;
          this.erroBusca = false;
        }),
        switchMap((termo) =>
          this.buscar(termo).pipe(
            // `catchError` DENTRO do switchMap: fora, o primeiro erro mataria a
            // stream e a busca pararia de funcionar até recarregar a página.
            catchError(() => {
              this.erroBusca = true;
              return of({ content: [], totalElements: 0 } as any);
            }),
          ),
        ),
        takeUntil(this.destruido$),
      )
      .subscribe((res: any) => {
        this.sugestoes = res?.content ?? [];
        this.total = res?.totalElements ?? 0;
        this.carregando = false;

        // Codigo de barras colado ou bipado: escolhe sozinho, sem esperar o
        // clique. A classificacao e a MESMA do clique (`ehLeituraDeCodigoDeBarras`:
        // resultado unico cujo campo `barras` e exatamente o termo), entao busca
        // por nome ou por codigo interno continua exigindo escolha do operador.
        const unico = this.sugestoes[0];
        if (unico && this.ehLeituraDeCodigoDeBarras(unico)) {
          this.autoEscolherPorCodigo(unico);
        }
      });
  }

  ngOnDestroy(): void {
    this.destruido$.next();
    this.destruido$.complete();
    this.termo$.complete();
  }

  /**
   * Mensagem única exibida abaixo do campo (contador de resultados e estados).
   *
   * Um só ponto de saída para não termos vários `role="status"` disputando o
   * leitor de tela. Retorna null quando não há nada a dizer.
   */
  get hint(): { chave: string; params?: any } | null {
    const raiz = 'contador_operar.venda.item_busca';

    if (this.erroBusca) return { chave: `${raiz}.erro` };
    if (this.carregando) return { chave: `${raiz}.buscando` };
    // Sem aviso de "digite N letras": com minLength=0 toda tecla busca.
    if (this.sugestoes.length > 0 && this.exibirContadorDeResultados) {
      return {
        chave: `${raiz}.resultados`,
        params: { mostrando: this.sugestoes.length, total: this.total },
      };
    }
    return null;
  }

  /** Item com preço cadastrado (o sem preço é sinalizado na sugestão). */
  temPreco(produto: any): boolean {
    return Number(produto?.precoDeVenda ?? 0) > 0;
  }

  // ===========================================================================
  // Busca
  // ===========================================================================

  /** `(completeMethod)` do p-autoComplete: só alimenta o pipeline RxJS. */
  onBuscar(event: { query: string }): void {
    this.ultimoTermo = event?.query ?? '';
    this.termo$.next(this.ultimoTermo);
  }

  /** "3*401" -> { quantidade: 3, codigo: '401' }. Qualquer outra forma e null. */
  private parseMultiplicador(termo: string): { quantidade: number; codigo: string } | null {
    const match = /^(\d+)\*(\w+)$/.exec((termo || '').trim());
    if (!match) return null;
    const quantidade = parseInt(match[1], 10);
    const codigo = match[2];
    if (!Number.isFinite(quantidade) || quantidade < 1 || !codigo) return null;
    return { quantidade, codigo };
  }

  /** Parte do termo que vai para a busca: sem o prefixo "<qtd>*". */
  private extrairTermoFiltro(termo: string): string {
    const t = (termo || '').trim();
    const match = /^\d+\*(.*)$/.exec(t);
    return (match ? match[1] : t).trim();
  }

  /**
   * Multiplicador valido para ESTE produto: o codigo digitado tem de ser
   * exatamente o codigo interno ou o de barras dele. "3*blusa" nao multiplica —
   * o operador pode estar so filtrando por nome.
   */
  private multiplicadorPara(produto: any): number | null {
    const m = this.parseMultiplicador(this.ultimoTermo);
    if (!m || !this.itemEscolhidoComQuantidade.observed) return null;
    const codigo = m.codigo.toLowerCase();
    const bate =
      String(produto?.codigoItem ?? '').toLowerCase() === codigo ||
      String(produto?.barras ?? '').toLowerCase() === codigo;
    return bate ? m.quantidade : null;
  }

  /**
   * O item escolhido veio de um LEITOR DE CÓDIGO DE BARRAS?
   *
   * O leitor age como teclado muito rápido: digita o código e emite Enter. O
   * evento não distingue isso de digitação humana, mas o RESULTADO sim. As três
   * condições precisam bater:
   *
   *   1. houve termo digitado (lista prévia, com termo vazio, nunca é bipe);
   *   2. a busca devolveu EXATAMENTE um item — código de barras é identificador
   *      único; com dois ou mais o operador escolhe, e aí é escolha visual;
   *   3. o termo casa o campo `barras`, não o nome nem o código interno.
   *
   * Conservador de propósito: na dúvida trata como escolha normal. Falso
   * negativo custa um Enter; falso positivo põe item na nota sem conferência.
   */
  private ehLeituraDeCodigoDeBarras(produto: any): boolean {
    const termo = this.extrairTermoFiltro(this.ultimoTermo ?? '');
    if (!termo) return false;
    if (this.total !== 1 || this.sugestoes.length !== 1) return false;

    const barras = String(produto?.barras ?? '').trim();
    return barras.length > 0 && barras.toLowerCase() === termo.toLowerCase();
  }

  /**
   * Item escolhido (Enter ou clique). Emite o objeto CRU e ramifica por origem:
   * bipe limpa o campo, escolha visual mantém o nome à vista.
   */
  onSelecionar(produto: any): void {
    if (!produto?.id) return;

    // Multiplicador tem prioridade: quem digitou "3*401" quer 3 de uma vez,
    // seja clicando na sugestao, seja com Enter.
    const quantidadeMultiplicada = this.multiplicadorPara(produto);
    if (quantidadeMultiplicada) {
      this.itemEscolhidoComQuantidade.emit({ produto, quantidade: quantidadeMultiplicada });
      this.limparSelecao();
      if (this.devolverFocoAposEscolher) {
        this.focarInput();
      }
      return;
    }

    // Bipe e escolha visual saem por Outputs DIFERENTES: o pai manda o bipe
    // direto ao carrinho e a escolha visual para a linha de lançamento.
    const bipe = this.ehLeituraDeCodigoDeBarras(produto);
    if (bipe) {
      this.itemEscolhidoViaCodigo.emit(produto);
    } else {
      this.itemEscolhido.emit(produto);
    }

    if (bipe) {
      // Bipe vai direto ao carrinho: não há nada "em composição", então o campo
      // fica limpo e pronto para o próximo código.
      this.limparSelecao();
    } else {
      // ⚠️ NÃO limpar o campo aqui: o item fica carregado na linha de
      // lançamento e o nome precisa continuar à vista, senão a linha exibe
      // qtd/preço/subtotal de um item não identificável. Com
      // `optionLabel="nome"` o PrimeNG já escreve o rótulo — basta não chamar
      // `clear()`. Quem limpa é `limparSelecao()`, acionada pelo pai.
      this.limparEstado();
    }

    // Com a linha de lançamento o foco vai para o campo Qtd, e quem o move é o
    // PAI — ver `devolverFocoAposEscolher`. Reafirmar o foco aqui venceria o
    // dele, porque isto roda dentro do `emit()` acima.
    if (this.devolverFocoAposEscolher) {
      this.focarInput();
      this.reabrirPrevia();
    }
  }

  /**
   * Escolha AUTOMÁTICA por código de barras (colado ou bipado), sem clique.
   *
   * Emite pelo mesmo Output do bipe e faz a MESMA limpeza do bipe via
   * clique/Enter em `onSelecionar()`: limpa o campo e reabre a prévia vazia com
   * o foco de volta, para o operador colar/bipar o próximo código em seguida
   * sem o termo anterior sobrando na tela.
   */
  private autoEscolherPorCodigo(produto: any): void {
    const quantidade = this.multiplicadorPara(produto);
    if (quantidade) {
      this.itemEscolhidoComQuantidade.emit({ produto, quantidade });
    } else {
      this.itemEscolhidoViaCodigo.emit(produto);
    }
    this.limparSelecao();
    if (this.devolverFocoAposEscolher) {
      this.focarInput();
      this.reabrirPrevia();
    }
  }

  /**
   * Limpeza PROGRAMÁTICA do campo, SEM emitir `selecaoLimpa`. Usada pelo pai ao
   * lançar a linha e pelo bipe.
   *
   * ⚠️ O silêncio evita recursão: o pai chama isto de dentro do `limparLinha()`
   * e reage a `selecaoLimpa` chamando `limparLinha()` de novo — e o `clear()` do
   * PrimeNG emite `(onClear)` de forma SÍNCRONA, fechando o ciclo na hora.
   *
   * `clear()` em vez de `termo = null`: sincroniza o ngModel e o value do DOM
   * sem depender do round-trip assíncrono do NgModel.
   */
  limparSelecao(): void {
    this.limpandoProgramaticamente = true;
    try {
      this.ac?.clear();
      this.limparEstado();
    } finally {
      this.limpandoProgramaticamente = false;
    }
  }

  /**
   * `(onClear)` do p-autoComplete — ÚNICO ponto que anuncia "não há mais item
   * selecionado", cobrindo o `×` e o backspace. Cala-se quando a limpeza partiu
   * daqui mesmo (ver `limparSelecao()`).
   */
  onCampoLimpo(): void {
    this.limparEstado();
    if (!this.limpandoProgramaticamente) {
      this.selecaoLimpa.emit();
    }
  }

  /**
   * Escreve um rótulo no campo SEM disparar busca.
   *
   * Quem normalmente escreve o nome é o p-autoComplete ao selecionar uma opção.
   * Quando o pai carrega um item por fora do dropdown (botão EDITAR) não há
   * seleção, e o campo ficaria vazio ao lado de uma faixa preenchida.
   *
   * Escreve nos DOIS lugares: no `ngModel` (para o `×` aparecer) e no `value` do
   * DOM — o PrimeNG mantém o texto exibido fora do model.
   */
  definirTextoSelecionado(texto: string): void {
    this.termo = texto ?? '';
    const input: HTMLInputElement | null =
      this.ac?.el?.nativeElement?.querySelector('input') ?? null;
    if (input) input.value = texto ?? '';
  }

  /**
   * Devolve o foco e reabre a lista prévia. Público para o pai chamar depois de
   * mandar uma linha ao carrinho — é o que sustenta o lançamento em rajada.
   */
  focarCampo(): void {
    this.focarInput();
    this.reabrirPrevia();
  }

  /**
   * Clique no `×`: limpa o campo, devolve a lista prévia e o foco. Também é o
   * "remover seleção" — sem avisar o pai sobrariam preço e subtotal órfãos na
   * linha.
   */
  limparCampo(): void {
    if (this.disabled) return;
    // O aviso ao pai sai do `onCampoLimpo()`, disparado pelo `(onClear)` do
    // `clear()` — ponto único, então `×` e backspace têm o mesmo efeito.
    this.ac?.clear();
    this.limparEstado();
    this.focarInput();
    // Sem `hide()`: limpar volta ao estado "campo ativo", que mostra a prévia.
    // Esconder para reabrir em seguida faria o painel piscar.
    this.reabrirPrevia();
  }

  /**
   * Zera o estado derivado da busca.
   *
   * Idempotente de propósito: é chamado tanto pelo `(onClear)` do PrimeNG
   * (usuário apagou tudo com backspace) quanto pelo `clear()` programático.
   */
  limparEstado(): void {
    this.sugestoes = [];
    this.total = 0;
    this.ultimoTermo = '';
    this.carregando = false;
    this.erroBusca = false;
  }

  /** Lupa dentro do campo: abre o modal completo (o fluxo antigo, intacto). */
  onAbrirLista(): void {
    if (this.disabled) return;
    this.abrirLista.emit();
  }

  /**
   * Rodapé "Ver lista completa" do dropdown.
   *
   * O `stopPropagation()` impede que o clique seja tratado como seleção de
   * opção pelo overlay; fechar o painel na mão evita que ele fique aberto por
   * cima do modal. Mesma sequência do `app-entity-selector`.
   */
  onVerListaCompleta(event: Event): void {
    event.stopPropagation();
    this.sugestoes = [];
    this.ac?.hide();
    this.abrirLista.emit();
  }

  /**
   * Rodapé "Criar novo item" do dropdown. Mesma sequência e mesmo motivo do
   * `onVerListaCompleta()` — aqui o painel ficaria por cima do diálogo.
   */
  onCriarItem(event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    this.sugestoes = [];
    this.ac?.hide();
    this.criarItem.emit();
  }

  // ===========================================================================
  // Privados
  // ===========================================================================

  /**
   * `GET /item/buscar` replicando o filtro OR do modal, com página curta
   * (dropdown, não tabela).
   *
   * ⚠️ NÃO trocar por `/item/buscarPorNomeOuBarrasOuCodigoItemOuCodigoFabricante`:
   * ele responde 404 quando não há resultado e 400 com termo em branco, o que é
   * incompatível com um type-ahead.
   *
   * O `encodeURIComponent` é o ÚNICO ponto de encoding do caminho:
   * `ApiService.buildQueryParams()` repassa o `filter` cru, então `&`, `#`, `+`,
   * `%` ou `=` no termo corromperiam a URL a cada 300ms. Escapa só o conteúdo —
   * os `=` e `&` estruturais ficam literais, como o backend espera.
   */
  private buscar(termo: string) {
    // "3*401" busca por "401": o multiplicador e para a adicao, nao para a busca.
    const t = encodeURIComponent(this.extrairTermoFiltro(termo));
    const params: any = { page: 0, size: 8, sort: 'nome,asc' };

    // Termo vazio (lista prévia) vai SEM os parâmetros de texto: a query
    // `ItemRepository.findByBuscaUnificada` tem um ramo `:nome IS NULL AND ...`
    // que cai direto no `WHERE i.empresaId`, sem LOWER(UNACCENT()) por linha.
    // Mandar `nome=&barras=...` funcionaria por acidente (`LIKE '%%'`), pagando
    // essa função no catálogo inteiro — caro porque `barras` não tem índice.
    if (t) {
      params.filter = `nome=${t}&barras=${t}&codigoItem=${t}&nomeGrupo=${t}&nomeSubGrupo=${t}`;
    }

    return this.itemService.get('/item/buscar', params);
  }

  /**
   * Reabre a lista prévia com o campo já focado.
   *
   * ⚠️ Precisa passar pelo `search()` do PrimeNG, não por `termo$.next('')`:
   * `handleSuggestionsChange()` — único ponto que chama `show()` — é guardado
   * por `if (this.loading)`, e quem liga `loading` é o `search()`. Atribuir
   * `sugestoes` por fora encheria a lista sem abrir o painel.
   *
   * `source = 'focus'` porque o minLength só se aplica à origem `'input'`.
   *
   * Usado após escolher um item e após o `×`: nos dois o input já está focado,
   * e sem evento de foco novo o `completeOnFocus` não dispararia.
   */
  private reabrirPrevia(): void {
    if (this.disabled) return;
    this.ac?.search(new Event('focus'), '', 'focus');
  }

  private focarInput(): void {
    this.ac?.el?.nativeElement?.querySelector('input')?.focus();
  }
}
